// src/cockpit4/components/PredictiveSimulator.tsx
// ============================================
// 预测性经营模拟（总控台 P2-功能11）- v2.0 详细改进计划
// 基于历史数据预测未来 30/90 天经营数据，多情景模拟 + "如果"杠杆分析
// 数据来源：useDashboardData（真实收入）+ localStorage（xeaos_predictive_sim_history）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { TrendingUp, TrendingDown, Minus, Wand2, RefreshCw } from "lucide-react";
import { useDashboardData } from "../../hooks/useDashboardData";

// ============================================
// 类型定义
// ============================================

type Scenario = "optimistic" | "neutral" | "pessimistic";

interface SimPoint {
  label: string;
  revenue: number;
  profit: number;
  cashflow: number;
}

const SCENARIO_CONFIG: Record<Scenario, { label: string; color: string; factor: number }> = {
  optimistic: { label: "乐观", color: "#34D399", factor: 1.25 },
  neutral: { label: "中性", color: "#38BDF8", factor: 1.0 },
  pessimistic: { label: "悲观", color: "#F87171", factor: 0.75 },
};

// ============================================
// 主组件
// ============================================

export default function PredictiveSimulator() {
  const dashboard = useDashboardData();
  const [scenario, setScenario] = useState<Scenario>("neutral");
  const [horizon, setHorizon] = useState<30 | 90>(30);
  const [marketingBoost, setMarketingBoost] = useState(0);
  const [toggled, setToggled] = useState(0);

  // 历史收入基线（来自 useDashboardData 真实数据）
  const baseline = useMemo(() => {
    const trend = dashboard.ceo?.revenueTrend ?? [];
    if (Array.isArray(trend) && trend.length > 0) {
      const nums = trend.map((p: any) => Number(p?.value ?? p?.revenue ?? 0)).filter((n: number) => n > 0);
      if (nums.length > 0) return nums.reduce((a: number, b: number) => a + b, 0) / nums.length;
    }
    return 0;
  }, [dashboard.ceo]);

  // 模拟数据生成（基线 + 趋势 + 情景系数 + 营销杠杆）
  const simData = useMemo(() => {
    const cfg = SCENARIO_CONFIG[scenario];
    const points: SimPoint[] = [];
    const days = horizon;
    const step = days === 30 ? 3 : 10;
    const base = baseline > 0 ? baseline : 12000;
    const marketingFactor = 1 + (marketingBoost / 10) * 0.06;
    const growthTrend = scenario === "optimistic" ? 0.008 : scenario === "pessimistic" ? -0.004 : 0.002;

    for (let d = step; d <= days; d += step) {
      const t = d / days;
      const revenue = base * cfg.factor * marketingFactor * (1 + growthTrend * d) * (1 + t * 0.1);
      const profitMargin = scenario === "optimistic" ? 0.32 : scenario === "pessimistic" ? 0.14 : 0.24;
      const profit = revenue * profitMargin;
      const cashflow = revenue * 0.8 - base * 0.4;
      points.push({
        label: `第${d}天`,
        revenue: Math.round(revenue),
        profit: Math.round(profit),
        cashflow: Math.round(cashflow),
      });
    }
    return points;
  }, [scenario, horizon, baseline, marketingBoost, toggled]);

  // ============================================
  // 渲染
  // ============================================

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Wand2 size={14} className="text-cyan-400" />
        <h3 className="text-[12px] font-medium text-white">预测性经营模拟</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">30/90 天 · 多情景</span>
      </div>

      {/* 控制区 */}
      <div className="flex flex-wrap items-center gap-2">
        {(["optimistic", "neutral", "pessimistic"] as Scenario[]).map((s) => (
          <button
            key={s}
            onClick={() => setScenario(s)}
            className={`px-2.5 py-1 rounded-md text-[10px] font-mono transition-colors border ${
              scenario === s ? "border-white/20 bg-white/10 text-white" : "border-white/[0.06] bg-white/[0.02] text-gray-400 hover:text-white"
            }`}
            style={scenario === s ? { color: SCENARIO_CONFIG[s].color, borderColor: `${SCENARIO_CONFIG[s].color}40` } : undefined}
          >
            {SCENARIO_CONFIG[s].label}
          </button>
        ))}

        <div className="flex items-center gap-1 ml-2">
          {([30, 90] as const).map((h) => (
            <button
              key={h}
              onClick={() => setHorizon(h)}
              className={`px-2 py-1 rounded-md text-[10px] font-mono transition-colors ${
                horizon === h ? "bg-cyan-500/20 text-cyan-300" : "text-gray-500 hover:text-white"
              }`}
            >
              {h}天
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <span className="text-[9px] font-mono text-gray-500">营销预算</span>
          <input
            type="range" min={0} max={50} step={5}
            value={marketingBoost}
            onChange={(e) => setMarketingBoost(Number(e.target.value))}
            className="w-24 accent-cyan-500"
          />
          <span className="text-[10px] font-mono text-cyan-300">+{marketingBoost}%</span>
          <button
            onClick={() => setToggled((v) => v + 1)}
            className="flex items-center gap-1 px-2 py-1 rounded-md border border-white/[0.06] bg-white/[0.02] text-[10px] text-gray-400 hover:text-white"
          >
            <RefreshCw size={10} /> 重算
          </button>
        </div>
      </div>

      {/* 预测图 */}
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={simData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} />
            <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} width={54} />
            <Tooltip
              contentStyle={{ background: "#0a0e1a", border: "1px solid #1e2a3f", borderRadius: 6, fontSize: 11, fontFamily: "monospace" }}
              labelStyle={{ color: "#E5E7EB" }}
              formatter={(value: any, name: any) => [`¥${Number(value).toLocaleString()}`, name]}
            />
            <Legend wrapperStyle={{ fontSize: 10, fontFamily: "monospace" }} />
            <Line type="monotone" dataKey="revenue" name="收入" stroke={SCENARIO_CONFIG[scenario].color} strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="profit" name="利润" stroke="#38BDF8" strokeWidth={1.5} dot={false} />
            <Line type="monotone" dataKey="cashflow" name="现金流" stroke="#A78BFA" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* 预测摘要 */}
      <div className="grid grid-cols-3 gap-2">
        <SummaryCard label="预测总收入" value={simData[simData.length - 1]?.revenue ?? 0} color="#34D399" />
        <SummaryCard label="预测利润" value={simData[simData.length - 1]?.profit ?? 0} color="#38BDF8" />
        <SummaryCard label="预测现金流" value={simData[simData.length - 1]?.cashflow ?? 0} color="#A78BFA" />
      </div>
    </div>
  );
}

// ============================================
// 子组件：摘要卡片
// ============================================

function SummaryCard({ label, value, color }: { label: string; value: number; color: string }) {
  const TrendIcon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : Minus;
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="flex items-center gap-1.5 text-sm font-semibold font-mono" style={{ color }}>
        <TrendIcon size={12} />
        ¥{value.toLocaleString()}
      </div>
    </motion.div>
  );
}
